import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Mail, Phone, Clock, Calendar, BarChart3, Users, TrendingUp, Sparkles,
  Plus, X, Send, Save, Bell, Globe, CheckCircle, AlertCircle, Zap,
  PieChart, Target, Award, Bot, MessageSquare, FileText, Settings2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WhatsApp Icon Component
const WhatsAppIcon = ({ size = 16, className = '' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Timezone options
const TIMEZONES = [
  { value: 'Asia/Dubai', label: 'Dubai (GMT+4)' },
  { value: 'Asia/Riyadh', label: 'Riyadh (GMT+3)' },
  { value: 'Asia/Kolkata', label: 'Mumbai (GMT+5:30)' },
  { value: 'Europe/London', label: 'London (GMT+0)' },
  { value: 'America/New_York', label: 'New York (GMT-5)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8)' },
  { value: 'Asia/Singapore', label: 'Singapore (GMT+8)' },
];

// Country codes
const COUNTRY_CODES = [
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'KSA', flag: '🇸🇦' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'USA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
];

const AutomatedReportSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    enabled: false,
    whatsapp_enabled: true,
    email_enabled: true,
    whatsapp_recipients: [],
    email_recipients: [],
    schedule_time: '09:00',
    timezone: 'Asia/Dubai',
    frequency: 'daily',
    include_ai_insights: true,
    include_lead_source: true,
    include_salesperson_performance: true,
    include_conversion_metrics: true,
    include_pipeline_summary: true,
    whatsapp_format: 'short',
    email_format: 'detailed',
  });

  // Input states
  const [newPhone, setNewPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+971');
  const [newEmail, setNewEmail] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');

  // Fetch settings
  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings/automated-reports?token=${token}`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch settings', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/settings/automated-reports?token=${token}`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTestReport = async () => {
    try {
      setSendingTest(true);
      await axios.post(`${API}/reports/send-test?token=${token}`, {
        whatsapp_enabled: settings.whatsapp_enabled,
        email_enabled: settings.email_enabled,
        whatsapp_recipients: settings.whatsapp_recipients,
        email_recipients: settings.email_recipients,
      });
      toast.success('Test report sent!');
    } catch (error) {
      toast.error('Failed to send test report');
    } finally {
      setSendingTest(false);
    }
  };

  // Phone validation
  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\s/g, '');
    if (cleaned.length < 7 || cleaned.length > 15) {
      return 'Invalid phone number';
    }
    if (!/^\d+$/.test(cleaned)) {
      return 'Phone must contain only numbers';
    }
    return '';
  };

  // Email validation
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(email)) {
      return 'Invalid email address';
    }
    return '';
  };

  // Add phone recipient
  const addPhoneRecipient = () => {
    const error = validatePhone(newPhone);
    if (error) {
      setPhoneError(error);
      return;
    }
    const fullNumber = `${phoneCountryCode}${newPhone.replace(/^0+/, '')}`;
    if (settings.whatsapp_recipients.includes(fullNumber)) {
      setPhoneError('Number already added');
      return;
    }
    setSettings(prev => ({
      ...prev,
      whatsapp_recipients: [...prev.whatsapp_recipients, fullNumber]
    }));
    setNewPhone('');
    setPhoneError('');
  };

  // Remove phone recipient
  const removePhoneRecipient = (phone) => {
    setSettings(prev => ({
      ...prev,
      whatsapp_recipients: prev.whatsapp_recipients.filter(p => p !== phone)
    }));
  };

  // Add email recipient
  const addEmailRecipient = () => {
    const error = validateEmail(newEmail);
    if (error) {
      setEmailError(error);
      return;
    }
    if (settings.email_recipients.includes(newEmail.toLowerCase())) {
      setEmailError('Email already added');
      return;
    }
    setSettings(prev => ({
      ...prev,
      email_recipients: [...prev.email_recipients, newEmail.toLowerCase()]
    }));
    setNewEmail('');
    setEmailError('');
  };

  // Remove email recipient
  const removeEmailRecipient = (email) => {
    setSettings(prev => ({
      ...prev,
      email_recipients: prev.email_recipients.filter(e => e !== email)
    }));
  };

  // Generate sample WhatsApp message
  const generateWhatsAppPreview = () => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    let message = `📊 *Daily Lead Report*\n📅 ${today}\n\n`;
    message += `🔹 New Leads: *127*\n\n`;
    
    // Leads assigned by agent
    message += `👥 *Leads Assigned by Agent*\n`;
    message += `• Ahmed K. - *32* leads\n`;
    message += `• Sara M. - *28* leads\n`;
    message += `• John D. - *18* leads\n`;
    message += `• Fatima A. - *11* leads\n\n`;
    
    // Lead Interest breakdown
    message += `🎯 *Lead Interest*\n`;
    message += `🔥 High: *34*\n`;
    message += `⚡ Medium: *52*\n`;
    message += `❄️ Low: *41*\n\n`;
    
    if (settings.include_salesperson_performance) {
      message += `🏆 *Top Performer*\nAhmed K. - 12 conversions\n\n`;
    }
    
    if (settings.include_lead_source) {
      message += `📈 *Top Sources*\n`;
      message += `1. Website - 45 leads\n`;
      message += `2. Referral - 32 leads\n`;
      message += `3. Social - 28 leads\n\n`;
    }
    
    if (settings.include_ai_insights) {
      message += `🤖 *AI Insight*\n`;
      message += `_"Lead volume increased 12% vs last week. Recommend focusing on website optimization."_\n\n`;
    }
    
    if (settings.include_conversion_metrics) {
      message += `📊 *Conversion Rate: 14.2%* ↑\n`;
    }
    
    return message;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Settings2 className="animate-spin text-slate-400" size={32} />
          <span className="text-slate-500 text-sm">Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="automated-reports-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <BarChart3 size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-900">Automated Reports</h1>
                <p className="text-sm text-slate-500">Configure daily lead performance reports</p>
              </div>
            </div>
            
            {/* AI Badge */}
            <div className="flex items-center gap-3">
              <Badge className="bg-violet-100 text-violet-700 border-violet-200 gap-1.5 px-3 py-1">
                <Sparkles size={12} />
                AI-Powered Insights
              </Badge>
              
              {/* Master Toggle */}
              <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                <Label htmlFor="master-toggle" className="text-sm font-medium text-slate-700">
                  Enable Reports
                </Label>
                <Switch
                  id="master-toggle"
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                  className="data-[state=checked]:bg-emerald-500"
                  data-testid="master-toggle"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-6">
          
          {/* Left Column - Configuration (7 cols) */}
          <div className="col-span-12 lg:col-span-7 space-y-6">
            
            {/* Recipients Section */}
            <Card className="p-6 border-slate-200" data-testid="recipients-section">
              <div className="flex items-center gap-2 mb-6">
                <Users size={18} className="text-slate-600" />
                <h2 className="text-lg font-semibold text-slate-900">Recipients</h2>
              </div>

              {/* WhatsApp Recipients */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <WhatsAppIcon size={16} className="text-green-600" />
                    <Label className="text-sm font-medium text-slate-700">WhatsApp Recipients</Label>
                  </div>
                  <Switch
                    checked={settings.whatsapp_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, whatsapp_enabled: checked }))}
                    className="data-[state=checked]:bg-green-500"
                    data-testid="whatsapp-toggle"
                  />
                </div>
                
                {settings.whatsapp_enabled && (
                  <>
                    <div className="flex gap-2 mb-3">
                      <Select value={phoneCountryCode} onValueChange={setPhoneCountryCode}>
                        <SelectTrigger className="w-[100px] bg-white" data-testid="country-code-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRY_CODES.map(c => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.flag} {c.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Phone number"
                        value={newPhone}
                        onChange={(e) => { setNewPhone(e.target.value); setPhoneError(''); }}
                        onKeyPress={(e) => e.key === 'Enter' && addPhoneRecipient()}
                        className={`flex-1 bg-white ${phoneError ? 'border-red-300 focus:ring-red-500' : ''}`}
                        data-testid="phone-input"
                      />
                      <Button 
                        onClick={addPhoneRecipient} 
                        variant="outline" 
                        className="gap-1.5 border-green-200 text-green-600 hover:bg-green-50"
                        data-testid="add-phone-btn"
                      >
                        <Plus size={14} /> Add
                      </Button>
                    </div>
                    {phoneError && (
                      <p className="text-xs text-red-500 mb-3 flex items-center gap-1">
                        <AlertCircle size={12} /> {phoneError}
                      </p>
                    )}
                    
                    {/* Phone chips */}
                    <div className="flex flex-wrap gap-2">
                      {settings.whatsapp_recipients.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">No recipients added</p>
                      ) : (
                        settings.whatsapp_recipients.map((phone, idx) => (
                          <Badge 
                            key={idx} 
                            className="bg-green-50 text-green-700 border-green-200 gap-1.5 pl-2 pr-1 py-1"
                            data-testid={`phone-chip-${idx}`}
                          >
                            <Phone size={12} />
                            {phone}
                            <button
                              onClick={() => removePhoneRecipient(phone)}
                              className="ml-1 hover:bg-green-200 rounded-full p-0.5 transition-colors"
                            >
                              <X size={12} />
                            </button>
                          </Badge>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>

              <Separator className="my-6" />

              {/* Email Recipients */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Mail size={16} className="text-purple-600" />
                    <Label className="text-sm font-medium text-slate-700">Email Recipients</Label>
                  </div>
                  <Switch
                    checked={settings.email_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, email_enabled: checked }))}
                    className="data-[state=checked]:bg-purple-500"
                    data-testid="email-toggle"
                  />
                </div>
                
                {settings.email_enabled && (
                  <>
                    <div className="flex gap-2 mb-3">
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        value={newEmail}
                        onChange={(e) => { setNewEmail(e.target.value); setEmailError(''); }}
                        onKeyPress={(e) => e.key === 'Enter' && addEmailRecipient()}
                        className={`flex-1 bg-white ${emailError ? 'border-red-300 focus:ring-red-500' : ''}`}
                        data-testid="email-input"
                      />
                      <Button 
                        onClick={addEmailRecipient} 
                        variant="outline" 
                        className="gap-1.5 border-purple-200 text-purple-600 hover:bg-purple-50"
                        data-testid="add-email-btn"
                      >
                        <Plus size={14} /> Add
                      </Button>
                    </div>
                    {emailError && (
                      <p className="text-xs text-red-500 mb-3 flex items-center gap-1">
                        <AlertCircle size={12} /> {emailError}
                      </p>
                    )}
                    
                    {/* Email chips */}
                    <div className="flex flex-wrap gap-2">
                      {settings.email_recipients.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">No recipients added</p>
                      ) : (
                        settings.email_recipients.map((email, idx) => (
                          <Badge 
                            key={idx} 
                            className="bg-purple-50 text-purple-700 border-purple-200 gap-1.5 pl-2 pr-1 py-1"
                            data-testid={`email-chip-${idx}`}
                          >
                            <Mail size={12} />
                            {email}
                            <button
                              onClick={() => removeEmailRecipient(email)}
                              className="ml-1 hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                            >
                              <X size={12} />
                            </button>
                          </Badge>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            </Card>

            {/* Schedule Section */}
            <Card className="p-6 border-slate-200" data-testid="schedule-section">
              <div className="flex items-center gap-2 mb-6">
                <Clock size={18} className="text-slate-600" />
                <h2 className="text-lg font-semibold text-slate-900">Schedule</h2>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Time</Label>
                  <Input
                    type="time"
                    value={settings.schedule_time}
                    onChange={(e) => setSettings(prev => ({ ...prev, schedule_time: e.target.value }))}
                    className="bg-white"
                    data-testid="time-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Timezone</Label>
                  <Select 
                    value={settings.timezone} 
                    onValueChange={(v) => setSettings(prev => ({ ...prev, timezone: v }))}
                  >
                    <SelectTrigger className="bg-white" data-testid="timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Frequency</Label>
                  <Select 
                    value={settings.frequency} 
                    onValueChange={(v) => setSettings(prev => ({ ...prev, frequency: v }))}
                  >
                    <SelectTrigger className="bg-white" data-testid="frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <p className="text-xs text-slate-500 mt-4 flex items-center gap-1.5">
                <Bell size={12} />
                Reports will be sent automatically at {settings.schedule_time} ({TIMEZONES.find(t => t.value === settings.timezone)?.label || settings.timezone})
              </p>
            </Card>

            {/* Report Content Section */}
            <Card className="p-6 border-slate-200" data-testid="content-section">
              <div className="flex items-center gap-2 mb-6">
                <FileText size={18} className="text-slate-600" />
                <h2 className="text-lg font-semibold text-slate-900">Report Content</h2>
              </div>

              <div className="space-y-4">
                {/* AI Insights */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-violet-50/50 border border-violet-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                      <Bot size={16} className="text-violet-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">AI Insights</p>
                      <p className="text-xs text-slate-500">Smart recommendations and trends</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.include_ai_insights}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, include_ai_insights: checked }))}
                    className="data-[state=checked]:bg-violet-500"
                    data-testid="toggle-ai-insights"
                  />
                </div>

                {/* Lead Source Performance */}
                <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Target size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">Lead Source Performance</p>
                      <p className="text-xs text-slate-500">Breakdown by acquisition channel</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.include_lead_source}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, include_lead_source: checked }))}
                    className="data-[state=checked]:bg-emerald-500"
                    data-testid="toggle-lead-source"
                  />
                </div>

                {/* Salesperson Performance */}
                <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Award size={16} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">Salesperson Performance</p>
                      <p className="text-xs text-slate-500">Top performers and rankings</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.include_salesperson_performance}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, include_salesperson_performance: checked }))}
                    className="data-[state=checked]:bg-emerald-500"
                    data-testid="toggle-salesperson"
                  />
                </div>

                {/* Conversion Metrics */}
                <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <TrendingUp size={16} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">Conversion Metrics</p>
                      <p className="text-xs text-slate-500">Conversion rates and trends</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.include_conversion_metrics}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, include_conversion_metrics: checked }))}
                    className="data-[state=checked]:bg-emerald-500"
                    data-testid="toggle-conversion"
                  />
                </div>

                {/* Pipeline Summary */}
                <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
                      <PieChart size={16} className="text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">Pipeline Summary</p>
                      <p className="text-xs text-slate-500">Overall pipeline health</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.include_pipeline_summary}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, include_pipeline_summary: checked }))}
                    className="data-[state=checked]:bg-emerald-500"
                    data-testid="toggle-pipeline"
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Previews (5 cols) */}
          <div className="col-span-12 lg:col-span-5 space-y-6">
            
            {/* WhatsApp Preview */}
            <Card className="overflow-hidden border-slate-200" data-testid="whatsapp-preview">
              <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3">
                <WhatsAppIcon size={20} className="text-white" />
                <span className="font-medium">WhatsApp Preview</span>
                <Badge className="ml-auto bg-white/20 text-white text-xs">Live Preview</Badge>
              </div>
              
              <div className="bg-[#ECE5DD] p-4 min-h-[400px]">
                {/* Chat bubble */}
                <div className="max-w-[85%] bg-white rounded-lg rounded-tl-none shadow-sm p-3">
                  <pre className="text-sm text-slate-800 whitespace-pre-wrap font-sans leading-relaxed">
                    {generateWhatsAppPreview()}
                  </pre>
                  <div className="flex items-center justify-end gap-1 mt-2 text-[10px] text-slate-400">
                    <span>9:00 AM</span>
                    <CheckCircle size={12} className="text-blue-500" />
                  </div>
                </div>
              </div>
            </Card>

            {/* Email Preview */}
            <Card className="overflow-hidden border-slate-200" data-testid="email-preview">
              <div className="bg-slate-800 text-white px-4 py-3 flex items-center gap-3">
                <Mail size={20} />
                <span className="font-medium">Email Preview</span>
                <Badge className="ml-auto bg-white/20 text-white text-xs">Detailed Report</Badge>
              </div>
              
              <div className="p-4 bg-slate-50">
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  {/* Email Header */}
                  <div className="border-b border-slate-100 p-4">
                    <p className="text-xs text-slate-500 mb-1">Subject:</p>
                    <p className="font-semibold text-slate-900">📊 Daily Lead Performance Report - {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                  
                  {/* Email Body Preview */}
                  <div className="p-4 space-y-4">
                    {/* Summary Section */}
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">127</p>
                      <p className="text-xs text-slate-500">New Leads Today</p>
                    </div>
                    
                    {/* Leads by Agent Section */}
                    <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
                      <p className="text-xs font-semibold text-violet-600 mb-2">👥 LEADS ASSIGNED BY AGENT</p>
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-700">Ahmed K.</span>
                          <span className="font-semibold text-violet-600">32 leads</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-700">Sara M.</span>
                          <span className="font-semibold text-violet-600">28 leads</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-700">John D.</span>
                          <span className="font-semibold text-violet-600">18 leads</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-700">Fatima A.</span>
                          <span className="font-semibold text-violet-600">11 leads</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Lead Interest Section */}
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-xs font-semibold text-slate-600 mb-2">🎯 LEAD INTEREST</p>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center p-2 bg-red-50 rounded">
                          <p className="text-lg font-bold text-red-600">34</p>
                          <p className="text-xs text-red-500">🔥 High</p>
                        </div>
                        <div className="text-center p-2 bg-amber-50 rounded">
                          <p className="text-lg font-bold text-amber-600">52</p>
                          <p className="text-xs text-amber-500">⚡ Medium</p>
                        </div>
                        <div className="text-center p-2 bg-sky-50 rounded">
                          <p className="text-lg font-bold text-sky-600">41</p>
                          <p className="text-xs text-sky-500">❄️ Low</p>
                        </div>
                      </div>
                    </div>

                    {/* AI Insight */}
                    {settings.include_ai_insights && (
                      <div className="p-3 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-lg border border-violet-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles size={14} className="text-violet-500" />
                          <span className="text-xs font-semibold text-violet-700">AI INSIGHT</span>
                        </div>
                        <p className="text-sm text-slate-700">
                          Lead volume increased 12% compared to last week. Website traffic is the primary driver. 
                          Recommend focusing on optimizing landing pages for higher conversion.
                        </p>
                      </div>
                    )}

                    {/* Chart Placeholder */}
                    <div className="h-24 bg-slate-100 rounded-lg flex items-center justify-center border border-dashed border-slate-300">
                      <div className="text-center text-slate-400">
                        <BarChart3 size={24} className="mx-auto mb-1" />
                        <p className="text-xs">Performance Charts</p>
                      </div>
                    </div>

                    {/* Top Performer */}
                    {settings.include_salesperson_performance && (
                      <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                          <Award size={18} className="text-amber-600" />
                        </div>
                        <div>
                          <p className="text-xs text-amber-600 font-semibold">TOP PERFORMER</p>
                          <p className="text-sm font-medium text-slate-800">Ahmed K. - 12 Conversions</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Sticky Footer */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            {settings.enabled ? (
              <>
                <CheckCircle size={16} className="text-emerald-500" />
                <span>Reports are <strong className="text-emerald-600">active</strong></span>
              </>
            ) : (
              <>
                <AlertCircle size={16} className="text-amber-500" />
                <span>Reports are <strong className="text-amber-600">paused</strong></span>
              </>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={sendTestReport}
              disabled={sendingTest || (settings.whatsapp_recipients.length === 0 && settings.email_recipients.length === 0)}
              className="gap-2"
              data-testid="test-report-btn"
            >
              {sendingTest ? (
                <Settings2 size={14} className="animate-spin" />
              ) : (
                <Send size={14} />
              )}
              Send Test Now
            </Button>
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="gap-2 bg-slate-900 hover:bg-slate-800"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <Settings2 size={14} className="animate-spin" />
              ) : (
                <Save size={14} />
              )}
              Save Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomatedReportSettings;
